import publicRoutes from "./public.routes.js";
import { COMPANY_JURISDICTIONS } from "@/constants/companyJurisdictions.js";
import { LICENSE_PRODUCTS } from "@/constants/licenseProducts.js";
import { useUiStore } from "@/stores/ui.store.js";

export const useListViewProps = (r) => {
  const limit = typeof r.query.limit === "string" ? Number(r.query.limit) : NaN;
  return {
    page: r.query.page ? Number(r.query.page) : 1,
    // Valid explicit URLs override the shared UI limit without changing it.
    limit: Number.isInteger(limit) && limit >= 1 && limit <= 500 ? limit : useUiStore().limit,
    filter: typeof r.query.filter === "string" ? r.query.filter : undefined,
  };
};
const routes = [
  ...publicRoutes,
  {
    path: "/updates",
    name: "Updates",
    component: () => import("@/pages/Changelog/UpdatesView.vue"),
    meta: { private: true },
  },
  {
    path: "/updates/:cuid",
    name: "UpdateDetail",
    component: () => import("@/pages/Changelog/UpdateDetailView.vue"),
    meta: { private: true },
  },
  {
    path: "/",
    redirect: "/home",
  },
  {
    path: "/waiting-list",
    name: "WaitingList",
    component: () => import("@/pages/SignupCompletion/WaitingList.vue"),
    meta: {
      private: true,
    },
  },
  {
    path: "/profile",
    name: "Profile",
    meta: {
      private: true,
      products: [],
    },
    component: () => import("@/pages/ProfileView.vue"),
  },
  {
    path: "/data-management/labels",
    name: "DataLabelList",
    component: () => import("@/pages/DataManagement/DataLabelListView.vue"),
    meta: {
      private: true,
      products: [],
    },
  },
  {
    path: "/home",
    name: "Home",
    meta: {
      private: true,
      products: [],
    },
  },
  {
    path: "/cbam",
    meta: {
      products: [LICENSE_PRODUCTS.CBAM],
    },
    children: [
      {
        path: "",
        redirect: "/cbam/overview",
      },
      {
        path: "overview",
        name: "Landing",
        props: useListViewProps,
        component: () => import("@/pages/LandingView.vue"),
        meta: {
          private: true,
        },
      },
    ],
  },
  {
    path: "/suppliers",
    meta: {
      products: [],
    },
    children: [
      {
        path: "",
        name: "SupplierOverview",
        component: () => import("@/pages/Suppliers/OverviewView.vue"),
        children: [
          {
            path: "",
            name: "SupplierList",
            props: useListViewProps,
            component: () => import("@/pages/Suppliers/ListView.vue"),
            meta: {
              private: true,
            },
          },
          {
            path: "new",
            name: "SupplierEdit",
            component: () => import("@/pages/Suppliers/ListView.vue"),
            meta: {
              private: true,
            },
          },
          {
            path: "invitations",
            name: "SupplierInvitationList",
            props: useListViewProps,
            component: () => import("@/pages/Suppliers/InvitationListView.vue"),
            meta: {
              private: true,
            },
          },
        ],
      },
      {
        path: ":cuid",
        name: "SupplierEditFlow",
        component: () => import("@/pages/Suppliers/EditView.vue"),
        children: [
          {
            path: "",
            name: "SupplierEditEntry",
            redirect: (to) => {
              return `/suppliers/${to.params.cuid}/details`;
            },
          },
          {
            path: "details",
            name: "SupplierEditDetails",
            component: () => import("@/pages/Suppliers/EditDetailsView.vue"),
            meta: {
              private: true,
            },
          },
          {
            path: "cbam",
            name: "SupplierEditCbam",
            props: useListViewProps,
            component: () => import("@/pages/Suppliers/EditCbamView.vue"),
            meta: {
              private: true,
              products: [LICENSE_PRODUCTS.CBAM],
            },
          },
        ],
      },
    ],
  },
  {
    path: "/attachments",
    name: "AttachmentList",
    props: useListViewProps,
    component: () => import("@/pages/Attachments/AttachmentListView.vue"),
    meta: {
      private: true,
      products: [LICENSE_PRODUCTS.CBAM, LICENSE_PRODUCTS.PPWR, LICENSE_PRODUCTS.CBAM_MRV],
    },
  },
  {
    path: "/importers",
    children: [
      {
        path: "",
        name: "ImportersOverview",
        component: () => import("@/pages/Importers/OverviewView.vue"),
        children: [
          {
            path: "",
            name: "ImportersList",
            props: useListViewProps,
            component: () => import("@/pages/Importers/ImporterListView.vue"),
            meta: {
              private: true,
            },
          },
          {
            path: "new",
            name: "ImporterCreation",
            component: () => import("@/pages/Importers/ImporterCreationView.vue"),
            meta: {
              private: true,
            },
          },
        ],
      },
      {
        path: ":cuid",
        name: "ImporterEditFlow",
        component: () => import("@/pages/Importers/EditView.vue"),
        children: [
          {
            path: "",
            name: "ImporterEditEntry",
            redirect: (to) => {
              return `/importers/${to.params.cuid}/overview`;
            },
          },
          {
            path: "overview",
            name: "ImporterOverview",
            component: () => import("@/pages/Importers/ImporterSummaryOverviewView.vue"),
            meta: {
              private: true,
            },
          },
          {
            path: "imports",
            name: "ImporterSummaryImports",
            props: useListViewProps,
            component: () => import("@/pages/Importers/ImporterSummaryImportsView.vue"),
            meta: {
              private: true,
            },
          },
          {
            path: "users",
            name: "ImporterSummaryUsers",
            props: useListViewProps,
            component: () => import("@/pages/Importers/ImporterSummaryUsersView.vue"),
            meta: {
              private: true,
            },
          },
          {
            path: "payments",
            name: "ImporterSummaryPayments",
            props: useListViewProps,
            component: () => import("@/pages/Importers/ImporterSummaryPaymentsView.vue"),
            meta: {
              private: true,
            },
          },
          {
            path: "guarantees",
            name: "ImporterSummaryGuarantees",
            props: useListViewProps,
            component: () => import("@/pages/Importers/ImporterSummaryGuaranteesView.vue"),
            meta: {
              private: true,
            },
          },
        ],
      },
    ],
    meta: {
      private: true,
      products: [LICENSE_PRODUCTS.CBAM],
    },
  },
  {
    path: "/imports",
    name: "ImportOverview",
    meta: {
      products: [LICENSE_PRODUCTS.CBAM],
    },
    component: () => import("@/pages/Imports/OverviewView.vue"),
    children: [
      {
        path: "",
        name: "ImportList",
        props: useListViewProps,
        component: () => import("@/pages/Imports/ListView.vue"),
        meta: {
          private: true,
        },
      },
      {
        path: "new",
        redirect: "/import-lines/manual",
      },
      {
        path: "uploader",
        redirect: "/import-lines/uploader/new",
      },

      // {
      //     path: ':cuid',
      //     redirect: (to) => `/import-lines/aggregate?type=import&import=${ to.params.cuid }`,
      // },
    ],
  },
  {
    path: "/imports",
    meta: {
      products: [LICENSE_PRODUCTS.CBAM],
    },
    children: [
      {
        path: ":cuid",
        name: "ImportEdit",
        component: () => import("@/pages/Imports/EditView.vue"),
        meta: {
          private: true,
        },
      },
    ],
  },
  {
    path: "/import-lines",
    name: "ImportLineOverview",
    meta: {
      products: [LICENSE_PRODUCTS.CBAM],
    },
    component: () => import("@/pages/Imports/OverviewView.vue"),
    children: [
      {
        path: "",
        name: "ImportLineList",
        props: (route) => ({
          ...useListViewProps(route),
          allTime: route.query.allTime === "true",
        }),
        component: () => import("@/pages/ImportLines/ImportLineListView.vue"),
        meta: {
          private: true,
        },
      },

      // {
      //     path: 'aggregate',
      //     name: "ImportLineAggregate",
      //     props: useListViewProps,
      //     component: () => import("@/pages/ImportLines/ImportLineAggregateView.vue"),
      //     meta: {
      //         private: true,
      //     },
      // },
    ],
  },
  {
    path: "/import-lines",
    meta: {
      products: [LICENSE_PRODUCTS.CBAM],
    },
    children: [
      {
        path: "uploader",
        redirect: "/import-lines/uploader/new",
      },
      {
        path: "new",
        redirect: "/import-lines/manual",
      },
      {
        path: "manual",
        component: () => import("@/pages/ImportLines/ImportLineCreateManualView.vue"),
        meta: {
          private: true,
        },
      },
      {
        path: "/import-lines/uploader/:cuid",
        name: "ImportLineUploader",
        component: () => import("@/pages/ImportLines/ImportLineUploaderView.vue"),
        meta: {
          private: true,
        },
      },
      {
        path: ":cuid",
        name: "ImportLineEdit",
        component: () => import("@/pages/ImportLines/ImportLineEditView.vue"),
        meta: {
          private: true,
        },
      },
    ],
  },
  // Redirect old insights routes to new ones (legacy support)
  {
    path: "/insights/:pathMatch(.*)*",
    redirect: (to) => ({
      path: to.path
        .replace("/insights/forecast/cbam/", "/cbam/insights/forecast/")
        .replace("/insights/cbam-explorer", "/cbam/insights/explorer")
        .replace("/insights", "/cbam/insights"),
      query: to.query,
      hash: to.hash,
    }),
  },
  {
    path: "/cbam",
    meta: {
      products: [LICENSE_PRODUCTS.CBAM],
    },
    children: [
      {
        path: "insights",
        component: () => import("@/pages/Insights/OverviewView.vue"),
        children: [
          {
            path: "",
            redirect: () => "/cbam/insights/imports",
          },
          {
            path: "imports",
            name: "ImportAnalytics",
            props: useListViewProps,
            component: () => import("@/pages/Insights/ImportsView.vue"),
            meta: {
              private: true,
            },
          },
          {
            path: "importers",
            name: "ImporterAnalytics",
            props: useListViewProps,
            component: () => import("@/pages/Insights/ImportersView.vue"),
            meta: {
              private: true,
            },
          },
          {
            path: "suppliers",
            name: "SupplierAnalytics",
            props: useListViewProps,
            component: () => import("@/pages/Insights/SuppliersView.vue"),
            meta: {
              private: true,
            },
          },
          {
            path: "forecast",
            name: "ImportForecasts",
            component: () => import("@/pages/Insights/ForecastRedirectView.vue"),
            meta: {
              private: true,
            },
          },
          {
            path: "forecast/eu",
            name: "EuCbamForecast",
            component: () => import("@/pages/Insights/EuForecastView.vue"),
            meta: {
              private: true,
            },
          },
          {
            path: "forecast/uk",
            name: "UkCbamForecast",
            component: () => import("@/pages/Insights/UkForecastView.vue"),
            meta: {
              private: true,
            },
          },
          {
            path: "explorer",
            name: "CbamExplorer",
            component: () => import("@/pages/Insights/CbamExplorerView.vue"),
            meta: {
              private: true,
              jurisdictions: [COMPANY_JURISDICTIONS.EU],
            },
          },
        ],
      },
    ],
  },
  {
    path: "/uploads",
    meta: {
      products: [],
    },
    component: () => import("@/pages/Uploads/OverviewView.vue"),
    props: useListViewProps,
    children: [
      {
        path: "",
        name: "UploadList",
        props: useListViewProps,
        component: () => import("@/pages/Uploads/UploadListView.vue"),
        meta: {
          private: true,
        },
      },
      {
        path: "field-maps",
        name: "UploadMapList",
        props: useListViewProps,
        component: () => import("@/pages/UploadMaps/UploadMapListView.vue"),
        meta: {
          private: true,
        },
      },
    ],
  },
  {
    path: "/upload-maps",
    meta: {
      products: [],
    },
    children: [
      {
        path: ":cuid",
        name: "UploadMapEdit",
        component: () => import("@/pages/UploadMaps/UploadMapEditView.vue"),
        meta: {
          private: true,
        },
      },
    ],
  },
  {
    path: "/installations",
    meta: {
      products: [LICENSE_PRODUCTS.CBAM, LICENSE_PRODUCTS.CBAM_MRV],
    },
    children: [
      {
        path: "",
        name: "InstallationList",
        props: useListViewProps,
        component: () => import("@/pages/Installations/InstallationListView.vue"),
        meta: {
          private: true,
        },
      },
      {
        path: ":cuid/schemas/:schemaCuid",
        name: "InstallationSchemaEdit",
        component: () => import("@/pages/Installations/InstallationSchemaEditView.vue"),
        meta: {
          private: true,
          products: [LICENSE_PRODUCTS.CBAM_MRV],
        },
      },
      {
        path: ":cuid",
        name: "InstallationEdit",
        component: () => import("@/pages/Installations/InstallationEditView.vue"),
        meta: {
          private: true,
        },
      },
    ],
  },
  {
    path: "/emissions",
    meta: {
      products: [LICENSE_PRODUCTS.CBAM, LICENSE_PRODUCTS.CBAM_MRV],
    },
    children: [
      {
        path: "",
        name: "EmissionList",
        props: useListViewProps,
        component: () => import("@/pages/Emissions/EmissionListView.vue"),
        meta: {
          private: true,
        },
      },
      {
        path: ":cuid",
        name: "EmissionEdit",
        component: () => import("@/pages/Emissions/EmissionEditView.vue"),
        meta: {
          private: true,
        },
      },
    ],
  },
  {
    path: "/production-processes",
    meta: {
      products: [LICENSE_PRODUCTS.CBAM, LICENSE_PRODUCTS.CBAM_MRV],
    },
    children: [
      {
        path: "",
        name: "ProductionProcessList",
        props: useListViewProps,
        component: () => import("@/pages/ProductionProcesses/ProductionProcessListView.vue"),
        meta: {
          private: true,
        },
      },
      {
        path: ":cuid",
        name: "ProductionProcessEdit",
        component: () => import("@/pages/ProductionProcesses/ProductionProcessEditView.vue"),
        meta: {
          private: true,
        },
      },
    ],
  },
  {
    path: "/products",
    meta: {
      products: [LICENSE_PRODUCTS.CBAM, LICENSE_PRODUCTS.PPWR],
    },
    children: [
      {
        path: "",
        name: "ProductList",
        props: useListViewProps,
        component: () => import("@/pages/Products/ProductListView.vue"),
        meta: {
          private: true,
        },
      },
      {
        path: ":cuid",
        name: "ProductEdit",
        component: () => import("@/pages/Products/ProductEditView.vue"),
        meta: {
          private: true,
        },
      },
    ],
  },
  {
    path: "/parameter-sets",
    meta: {
      products: [LICENSE_PRODUCTS.CBAM, LICENSE_PRODUCTS.CBAM_MRV],
    },
    children: [
      {
        path: "",
        name: "ParameterSetList",
        props: useListViewProps,
        component: () => import("@/pages/ParameterSets/ParameterSetListView.vue"),
        meta: {
          private: true,
        },
      },
      {
        path: ":cuid",
        name: "ParameterSetEdit",
        component: () => import("@/pages/ParameterSets/ParameterSetEditView.vue"),
        meta: {
          private: true,
        },
      },
    ],
  },
  {
    path: "/data-requests",
    meta: {
      products: [LICENSE_PRODUCTS.CBAM, LICENSE_PRODUCTS.CBAM_MRV, LICENSE_PRODUCTS.PPWR],
    },
    children: [
      {
        path: "",
        name: "DataRequestList",
        component: () => import("@/pages/DataRequests/DataRequestListView.vue"),
        props: useListViewProps,
        meta: {
          private: true,
        },
      },
      {
        path: ":cuid",
        name: "DataRequestEdit",
        component: () => import("@/pages/DataRequests/DataRequestEditView.vue"),
        meta: {
          private: true,
        },
      },
    ],
  },
  {
    path: "/guarantees",
    meta: {
      products: [LICENSE_PRODUCTS.CBAM],
    },
    children: [
      {
        path: "",
        name: "GuaranteesList",
        component: () => import("@/pages/Guarantees/GuaranteeListView.vue"),
        props: useListViewProps,
        meta: {
          private: true,
        },
      },
      {
        path: ":cuid",
        name: "GuaranteesEdit",
        component: () => import("@/pages/Guarantees/GuaranteeEditView.vue"),
        meta: {
          private: true,
        },
      },
    ],
  },
  {
    path: "/payments",
    meta: {
      products: [LICENSE_PRODUCTS.CBAM],
    },
    children: [
      {
        path: "",
        name: "PaymentsList",
        component: () => import("@/pages/Payments/PaymentListView.vue"),
        props: useListViewProps,
        meta: {
          private: true,
        },
      },
      {
        path: ":cuid",
        name: "PaymentsEdit",
        component: () => import("@/pages/Payments/PaymentEditView.vue"),
        meta: {
          private: true,
        },
      },
    ],
  },
  {
    path: "/ppwr",
    meta: {
      products: [LICENSE_PRODUCTS.PPWR],
    },
    children: [
      {
        path: "articles",
        name: "ArticleList",
        component: () => import("@/pages/Articles/ArticleListView.vue"),
        props: useListViewProps,
        meta: { private: true },
      },
      {
        path: "articles/:cuid",
        name: "ArticleEdit",
        component: () => import("@/pages/Articles/ArticleEditView.vue"),
        meta: { private: true },
      },
      {
        path: "",
        redirect: "/ppwr/overview",
      },
      {
        path: "overview",
        name: "PpwrOverview",
        component: () => import("@/pages/PpwrOverview/PpwrOverviewView.vue"),
        meta: {
          private: true,
        },
      },
      {
        path: "insights",
        name: "PpwrInsights",
        props: useListViewProps,
        component: () => import("@/pages/Insights/PpwrView.vue"),
        meta: {
          private: true,
        },
      },
      {
        path: "packaging",
        children: [
          {
            path: "",
            name: "PpwrPackagingList",
            component: () => import("@/pages/PpwrPackaging/PpwrPackagingListView.vue"),
            props: useListViewProps,
            meta: {
              private: true,
            },
          },
          {
            path: ":cuid",
            name: "PpwrPackagingEdit",
            component: () => import("@/pages/PpwrPackaging/PpwrPackagingEditView.vue"),
            meta: {
              private: true,
            },
          },
        ],
      },
      {
        path: "declaration",
        children: [
          {
            path: "",
            name: "PpwrDeclarationList",
            component: () => import("@/pages/PpwrDeclaration/PpwrDeclarationListView.vue"),
            props: useListViewProps,
            meta: {
              private: true,
            },
          },
          {
            path: ":cuid",
            name: "PpwrDeclarationDetail",
            component: () => import("@/pages/PpwrDeclaration/PpwrDeclarationDetailView.vue"),
            meta: {
              private: true,
            },
          },
        ],
      },
    ],
  },
  {
    path: "/process-records",
    meta: {
      products: [LICENSE_PRODUCTS.CBAM, LICENSE_PRODUCTS.CBAM_MRV],
    },
    children: [
      {
        path: "",
        name: "ProcessRecordList",
        component: () => import("@/pages/ProcessRecords/ProcessRecordListView.vue"),
        props: useListViewProps,
        meta: {
          private: true,
        },
      },
      {
        path: ":cuid",
        name: "ProcessRecordEdit",
        component: () => import("@/pages/ProcessRecords/ProcessRecordEditView.vue"),
        meta: {
          private: true,
        },
      },
    ],
  },
  {
    path: "/cbam",
    meta: {
      products: [LICENSE_PRODUCTS.CBAM],
    },
    children: [
      {
        path: "declarations",
        children: [
          {
            path: "",
            name: "XmlReportList",
            component: () => import("@/pages/XmlReports/XmlReportListView.vue"),
            props: useListViewProps,
            meta: {
              private: true,
            },
          },
          {
            path: ":cuid",
            name: "XmlReportEdit",
            component: () => import("@/pages/XmlReports/XmlReportEditView.vue"),
            meta: {
              private: true,
            },
          },
        ],
      },
    ],
  },
  {
    path: "/reports",
    name: "ReportList",
    component: () => import("@/pages/Reports/ReportListView.vue"),
    props: useListViewProps,
    meta: {
      private: true,
      products: [LICENSE_PRODUCTS.CBAM],
    },
    children: [
      {
        path: ":cuid",
        name: "ReportEdit",
        component: () => import("@/pages/Reports/ReportEditView.vue"),
        meta: {
          private: true,
        },
      },
    ],
  },
  {
    path: "/settings",
    component: () => import("@/pages/Settings/SettingsView.vue"),
    children: [
      {
        path: "",
        name: "SettingsEntry",
        redirect: "/settings/details",
      },
      {
        path: "details",
        name: "SettingsDetails",
        component: () => import("@/pages/Settings/ManageCompanyDetails.vue"),
        meta: {
          private: true,
        },
      },
      {
        path: "team",
        name: "SettingsTeam",
        component: () => import("@/pages/Settings/ManageTeamView.vue"),
        meta: {
          private: true,
        },
      },
      {
        path: "api-keys",
        name: "SettingsApiKeys",
        component: () => import("@/pages/Settings/ApiKeysManagementView.vue"),
        meta: {
          private: true,
        },
      },
    ],
    meta: {
      private: true,
      products: [],
    },
  },
  // 404 page
  {
    path: "/:pathMatch(.*)*",
    name: "404Handler",
    component: () => import("@/pages/NotFoundView.vue"),
  },
];

export default routes;
